import { PlusOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Segmented, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import {
  createLeadNote,
  deleteLeadNote,
  updateLeadNote
} from "../../redux/lead-notes.thunks.ts"
import { noteToggledPinOptimistic } from "../../redux/lead.slice.ts"
import { type Lead, type LeadNote } from "../../types/lead.types.ts"
import { type NoteFormValues, NoteFormModal } from "./NoteFormModal.tsx"
import { NotesBoardView } from "./NotesBoardView.tsx"
import { NotesListView } from "./NotesListView.tsx"
import { sortNotes } from "./note-sort.ts"

const { Text } = Typography

type NotesViewMode = "board" | "list"

interface LeadNotesSectionProps {
  lead: Lead
  canEdit?: boolean
  className?: string
}

/**
 * Replaces the single rich-text Internal Notes card with a multi-note board.
 * `lead.notes` arrives already populated on the lead-fetch payload — there is
 * deliberately no separate fetch-on-mount here, to avoid a duplicate request.
 */
function LeadNotesSection({ lead, canEdit, className }: LeadNotesSectionProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [view, setView] = useState<NotesViewMode>("board")
  const [modalTarget, setModalTarget] = useState<LeadNote | "new" | null>(null)

  const notes = lead.notes ?? []
  // One canonical sorted array, computed once and handed to whichever view is
  // active — Board and List must never disagree on order.
  const sorted = useMemo(() => sortNotes(notes), [notes])

  const handleSubmit = async (values: NoteFormValues) => {
    if (modalTarget === "new") {
      await dispatch(createLeadNote({ leadId: lead.id, ...values })).unwrap()
    } else if (modalTarget) {
      await dispatch(updateLeadNote({ id: modalTarget.id, ...values })).unwrap()
    }
  }

  const handleDelete = (id: string) => {
    void dispatch(deleteLeadNote(id))
  }

  const handleTogglePin = async (note: LeadNote) => {
    const next = !note.is_pinned
    dispatch(noteToggledPinOptimistic({ id: note.id, is_pinned: next }))
    try {
      await dispatch(updateLeadNote({ id: note.id, is_pinned: next })).unwrap()
    } catch {
      // The write failed — put the flag back the way it was.
      dispatch(
        noteToggledPinOptimistic({ id: note.id, is_pinned: note.is_pinned })
      )
    }
  }

  const addButton = canEdit && (
    <Button
      type={"primary"}
      size={"small"}
      icon={<PlusOutlined />}
      onClick={() => setModalTarget("new")}
      aria-label={t("leadWorkspace.cards.notes.add")}
    >
      {t("leadWorkspace.cards.notes.add")}
    </Button>
  )

  return (
    <Card
      size={"small"}
      className={className}
      data-testid={"lead-notes-section"}
      title={t("leadWorkspace.cards.notes.title", { count: sorted.length })}
      extra={
        <Flex gap={8} align={"center"}>
          <Segmented<NotesViewMode>
            size={"small"}
            value={view}
            onChange={setView}
            options={[
              {
                label: t("leadWorkspace.cards.notes.boardView"),
                value: "board"
              },
              { label: t("leadWorkspace.cards.notes.listView"), value: "list" }
            ]}
          />
          {addButton}
        </Flex>
      }
    >
      {sorted.length === 0 ? (
        <Flex
          vertical
          align={"center"}
          justify={"center"}
          gap={4}
          className={"py-10"}
        >
          <Text strong>{t("leadWorkspace.cards.notes.emptyTitle")}</Text>
          <Text type={"secondary"} className={"max-w-xs text-center"}>
            {t("leadWorkspace.cards.notes.emptyDescription")}
          </Text>
          {canEdit && (
            <Button
              type={"primary"}
              icon={<PlusOutlined />}
              onClick={() => setModalTarget("new")}
              className={"mt-2"}
              aria-label={t("leadWorkspace.cards.notes.add")}
            >
              {t("leadWorkspace.cards.notes.add")}
            </Button>
          )}
        </Flex>
      ) : view === "board" ? (
        <NotesBoardView
          notes={sorted}
          canEdit={canEdit}
          onEdit={setModalTarget}
          onDelete={handleDelete}
          onTogglePin={handleTogglePin}
        />
      ) : (
        <NotesListView
          notes={sorted}
          canEdit={canEdit}
          onEdit={setModalTarget}
          onDelete={handleDelete}
          onTogglePin={handleTogglePin}
        />
      )}

      {modalTarget && (
        <NoteFormModal
          note={modalTarget === "new" ? null : modalTarget}
          onClose={() => setModalTarget(null)}
          onSubmit={handleSubmit}
        />
      )}
    </Card>
  )
}

export { LeadNotesSection }
